/**
 * A vocabulary picker that can FILL ITSELF.
 *
 * The bug this exists for: a fresh deployment's `ACTIVITY_TYPE` vocabulary is
 * empty, so «نوع فعالیت» on the calendar offered nothing, and there was no
 * screen anywhere in the app that could add one — the only ways in were a seed
 * script with database access or a hand-written GraphQL mutation. A required
 * field with no reachable values is a dead end, not a form.
 *
 * The rules it keeps:
 *
 * - **An empty vocabulary opens the creator by itself.** Somebody who came here
 *   to book a meeting should not have to work out that the «+» is what unblocks
 *   them; the input is already there with the cursor in it.
 * - **One field: the label.** The `code` is derived (`lib/lookupCode.js`) and
 *   shown, not asked for. It is immutable after creation and the settings
 *   screens exist for anyone who cares which one they get.
 * - **The control is absent for a role that cannot use it.** Creating a
 *   vocabulary row is ADMIN at the BFF and again at the service, so a USER gets
 *   a sentence naming what to ask for instead of a button that returns
 *   «دسترسی ندارید» (CLAUDE.md §12 — a control that cannot help is its own kind
 *   of lie).
 * - **The new row is SELECTED, not merely created.** The mutation resolves
 *   after its refetch, so the option exists by the time the value points at it.
 *
 * Product-neutrality (CLAUDE.md §1): nothing here names a vocabulary or a
 * value. The caller supplies the options, the mutation and a code prefix.
 */
import { Button } from '@meshhub/design-tokens/ui';
import { Plus, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { MutationError, SelectField, TextField } from './FormKit.web.jsx';
import { codeFromLabel } from '../lib/lookupCode.js';
import { S } from '../lib/strings.js';

export function VocabularyField({
  label,
  value,
  onChange,
  options = [],
  loading = false,
  includeNone = false,
  noneLabel = S.app.none,
  required = false,
  hint,
  canCreate = false,
  codePrefix = 'CUSTOM',
  onCreate,
  creating = false,
  createError,
}) {
  const empty = options.length === 0;
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState('');
  const inputRef = useRef(null);

  // An empty REQUIRED vocabulary is the whole reason this component exists:
  // open the creator rather than making the user find it. Only while it IS
  // empty — reopening it after the first row is added would fight a typist.
  //
  // REQUIRED IS THE CONDITION, and it was missing. The creator focuses its own
  // input, so an OPTIONAL empty picker stole the cursor from whatever the user
  // was actually typing — on quick-add, «محصول مورد علاقه» with no products
  // seeded grabbed focus away from the surname, which is the one field that
  // form exists to capture. A required picker with nothing in it is a dead end
  // worth interrupting for; an optional one is not, and it still shows the
  // empty notice and the «گزینه تازه» button.
  useEffect(() => {
    if (required && empty && canCreate && !loading) setAdding(true);
  }, [required, empty, canCreate, loading]);

  useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const code = codeFromLabel(draft, codePrefix);
  const canSubmit = Boolean(draft.trim()) && !creating;

  async function submit() {
    if (!canSubmit) return;
    const created = await onCreate({ labelFa: draft.trim(), code });
    const id = typeof created === 'string' ? created : created?.id;
    if (!id) return;
    onChange(id);
    setDraft('');
    setAdding(false);
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-end gap-2">
        <SelectField
          label={label}
          hint={hint}
          value={value ?? ''}
          required={required}
          className="flex-1"
          onChange={(event) => onChange(event.target.value)}
        >
          {(includeNone || empty) && <option value="">{empty ? S.vocabulary.none : noneLabel}</option>}
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.labelFa}
            </option>
          ))}
        </SelectField>

        {canCreate && !adding && (
          <Button
            type="button"
            variant="ghost"
            className="mb-1 gap-1"
            title={S.vocabulary.addNew}
            onClick={() => setAdding(true)}
          >
            <Plus size={15} aria-hidden="true" />
            {S.vocabulary.addNewShort}
          </Button>
        )}
      </div>

      {adding && (
        <div className="flex flex-col gap-2 rounded border border-border bg-surface p-3">
          <div className="flex items-end gap-2">
            <TextField
              ref={inputRef}
              className="flex-1"
              label={S.vocabulary.newLabel}
              hint={draft.trim() ? S.vocabulary.codeIs(code) : S.vocabulary.newHint}
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              // Enter here means "add this option", NOT "submit the activity" —
              // the surrounding FormDialog is a <form>, so without this the key
              // that most obviously finishes this little input would save a
              // half-filled activity instead.
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                submit();
              }}
            />
            <Button
              type="button"
              variant="primary"
              className="mb-1"
              disabled={!canSubmit}
              onClick={submit}
            >
              {creating ? S.app.saving : S.app.add}
            </Button>
            {!empty && (
              <Button
                type="button"
                variant="ghost"
                className="mb-1"
                title={S.app.cancel}
                aria-label={S.app.cancel}
                onClick={() => {
                  setDraft('');
                  setAdding(false);
                }}
              >
                <X size={15} aria-hidden="true" />
              </Button>
            )}
          </div>
          <MutationError error={createError} />
        </div>
      )}

      {empty && !canCreate && !loading && (
        <p className="text-xs text-text-muted">{S.vocabulary.emptyUser}</p>
      )}
    </div>
  );
}
